import { Geotab, StatusData } from "@fairfleet/geotab";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useAddInState } from "@/components/AddInStateProvider";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { useGroups } from "@/hooks/groups/useGroups";
import { OFFLINE_SIGNAL_AGE_MS } from "@/pages/tachograph/utilities/tachographStatus";
import { groupIdsWithDescendants } from "@/utilities/groupIndex";
import {
  DEFAULT_STATUS_DATA_CONCURRENCY,
  fetchStatusDataComplete,
  StatusDataFetchTask,
} from "@/utilities/query/fetchStatusDataComplete";

/**
 * The window is the age at which a tachograph counts as offline: a reading older than that
 * makes no difference to the connectivity status. A vehicle with none in this window shows no
 * value rather than widening the window, which would cost another fleet-sized request.
 */
const WINDOW_MS = OFFLINE_SIGNAL_AGE_MS;

/** The readings change rarely, so a returning tab does not need to re-run the reads. */
const STALE_TIME_MS = 60 * 60 * 1000;

/**
 * Most series asked for one vehicle at a time. Up to this many fit one client queue flush, so a
 * small group costs a single `ExecuteMultiCall`. A larger group asks one series per diagnostic
 * instead, which costs a few calls whatever the group size: narrowed to the selected groups
 * (`StatusDataSearch.deviceSearch.groups`) when the filter is a plain group list, fleet-wide
 * otherwise (no filter, or an advanced filter condition, which DeviceSearch cannot express).
 *
 * The device list includes the vehicles of subgroups, but `DeviceSearch.groups` is not documented
 * to, so the selected groups are sent together with all their descendants. Without a complete
 * group tree (failed or truncated) the series stay fleet-wide rather than drop subgroup vehicles.
 */
export const TACHOGRAPH_PER_DEVICE_SERIES_LIMIT = DEFAULT_STATUS_DATA_CONCURRENCY;

/** What the group filter allows the server-side search to narrow to. */
export interface TachographStatusDataScope {
  /**
   * Ids of the selected groups and all their descendants; omitted when the filter is not a plain
   * group list or the group tree is not complete.
   */
  groupIds?: string[];
}

function buildTasks(
  diagnosticIds: string[],
  deviceIds: string[],
  { groupIds }: TachographStatusDataScope,
): StatusDataFetchTask[] {
  if (deviceIds.length * diagnosticIds.length > TACHOGRAPH_PER_DEVICE_SERIES_LIMIT) {
    return diagnosticIds.map(diagnosticId => ({
      key: diagnosticId,
      diagnosticId,
      ...(groupIds?.length ? { groupIds } : {}),
    }));
  }
  return deviceIds.flatMap(deviceId =>
    diagnosticIds.map(diagnosticId => ({
      key: `${deviceId}:${diagnosticId}`,
      deviceId,
      diagnosticId,
    })),
  );
}

/**
 * Readings of the vehicles in `deviceIds` (the group filter's vehicles), paged until the window
 * is covered. Callers pick the newest reading per vehicle themselves.
 *
 * The id filter stays on for group and fleet-wide series: they can return vehicles the device
 * list left out.
 *
 * Readings without an id are dropped: a search by vehicle and diagnostic returns interpolated
 * values at the window's edges when no stored record exists there, and those carry no id. One at
 * `toDate` would look like a reading sent just now.
 */
export async function fetchTachographStatusData(
  geotabClient: Geotab,
  diagnosticIds: string[],
  deviceIds: string[],
  scope: TachographStatusDataScope = {},
  signal?: AbortSignal,
  windowMs = WINDOW_MS,
): Promise<StatusData[]> {
  const toDate = new Date();
  const wantedDeviceIds = new Set(deviceIds);
  const collected: StatusData[] = [];

  await fetchStatusDataComplete(geotabClient, {
    tasks: buildTasks(diagnosticIds, deviceIds, scope),
    fromDate: new Date(toDate.getTime() - windowMs),
    toDate,
    onPage: (_key, page) => {
      for (const row of page) {
        if (row.id && row.device?.id && wantedDeviceIds.has(row.device.id)) collected.push(row);
      }
    },
    signal,
  });

  return collected;
}

/**
 * Readings of the given tachograph diagnostics for the given vehicles.
 *
 * GetLatestStatusDataByDevice does not carry these — it answers with the device row and an empty
 * sensor list for tachograph diagnostics — so the readings of the last 14 days are fetched.
 */
export function useTachographDeviceStatusData(
  diagnosticIds: string[],
  deviceIds: string[],
  /** Shorter for readings sent so often that two weeks of them would be a fleet-sized download. */
  windowMs = WINDOW_MS,
) {
  const { geotabClient, activeSessionId } = useGeotabClient();
  const { groups } = useAddInState();
  const groupTree = useGroups();
  const hasGroupSelection = !!groups && groups.length > 0;
  const groupTreeComplete = !groupTree.isError && !groupTree.isTruncated && !!groupTree.data;
  // Same precedence as createGroupFilter, which scoped the device list: a plain group selection
  // wins over the advanced filter. Without one, or without a complete group tree to add the
  // subgroups from, the large-group fallback stays fleet-wide.
  const groupIds = useMemo(
    () =>
      hasGroupSelection && groupTreeComplete
        ? groupIdsWithDescendants(
            groups.map(group => group.id),
            groupTree.data,
          )
        : undefined,
    [hasGroupSelection, groupTreeComplete, groups, groupTree.data],
  );

  return useQuery({
    queryKey: ["tachographDeviceStatusData", activeSessionId, diagnosticIds, deviceIds, groupIds, windowMs],

    queryFn: async ({ signal }) =>
      fetchTachographStatusData(geotabClient, diagnosticIds, deviceIds, { groupIds }, signal, windowMs),

    enabled:
      diagnosticIds.length > 0 && deviceIds.length > 0 && !(hasGroupSelection && groupTree.isPending),
    staleTime: STALE_TIME_MS,
    refetchOnWindowFocus: false,
  });
}
